import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import NoOrganization from '@/pages/NoOrganization';

/** Router state RequireAuth gives /login: the location it was asked for. */
export interface LoginState {
  from?: string;
}

/** The signed-in app's gate: a session, and an organization to act in. */
export default function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { pathname, search, hash } = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
      </div>
    );
  }

  // Keep where the user was going (a deep link, an emailed boot-approval
  // link): Login hands it to Keycloak as returnTo, and /auth/callback comes
  // back to it through callbackDestination (#1155).
  if (!isAuthenticated) {
    const from: LoginState = { from: pathname + search + hash };
    return <Navigate to="/login" replace state={from} />;
  }

  // Signed in, but in no organization and not staff (JSS#849 follow-up): the
  // BFF refuses every org-scoped call, so say why instead of rendering the app.
  if (user?.noOrganization) {
    return <NoOrganization />;
  }

  return <>{children}</>;
}
